import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormLabel,
  FormControl,
  Link,
  TextField,
  Typography,
  Stack,
  Card as MuiCard,
  AppBar,
  Toolbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AppTheme from "../theme/AppTheme";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import userApi from "../api/userApi";
import { useNavigate, useParams } from "react-router-dom";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 90dvh)",
  minHeight: "90%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

function ChangePasswordPage(props) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
  const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] =
    React.useState("");
  const dispatch = useDispatch();
  const { token } = useParams();
  const [usId, setUsId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const checkToken = async () => {
      try {
        const response = await userApi.CheckTokenChangePassword(token);
        if (response.success) {
          setUsId(response.data); // lấy UsId
          setTokenError(false);
        } else {
          setTokenError(true);
          setErrorMessage(
            response.message || "Liên kết không hợp lệ hoặc đã hết hạn."
          );
        }
      } catch (err) {
        console.error("Check token failed:", err);
        setTokenError(true);
        setErrorMessage("Có lỗi xảy ra khi kiểm tra liên kết.");
      } finally {
        setLoading(false);
      }
    };
    checkToken();
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (passwordError || confirmPasswordError) {
      return;
    }
    try {
      const response = await userApi.ChangePasswordByEmailAuth(usId, password);
      if (response.success) {
        setSuccess(true);
        setSuccessMessage(response.message || "Đổi mật khẩu thành công!");
      } else {
        toast.error(response.message || "Đổi mật khẩu thất bại.");
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi đổi mật khẩu.");
      console.error("Change password failed:", error);
    } finally {
    }
  };

  const validateInputs = () => {
    let isValid = true;
    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Mật khẩu phải có ít nhất 6 ký tự.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }
    if (!confirmPassword || confirmPassword.length < 6) {
      setConfirmPasswordError(true);
      setConfirmPasswordErrorMessage("Mật khẩu phải có ít nhất 6 ký tự.");
      isValid = false;
    } else {
      setConfirmPasswordError(false);
      setConfirmPasswordErrorMessage("");
    }
    if (isValid) {
      if (password != confirmPassword) {
        setConfirmPasswordError(true);
        setConfirmPasswordErrorMessage("Xác nhận mật khẩu không khớp.");
        isValid = false;
      } else {
        setConfirmPasswordError(false);
        setConfirmPasswordErrorMessage("");
      }
    }
    return isValid;
  };
  if (loading) {
    return (
      <AppTheme {...props}>
        <SignInContainer direction="column" justifyContent="center">
          <Typography variant="h6" align="center">
            Đang kiểm tra liên kết...
          </Typography>
        </SignInContainer>
      </AppTheme>
    );
  }
  if (tokenError) {
    return (
      <AppTheme {...props}>
        <SignInContainer direction="column" justifyContent="center">
          <Card variant="outlined">
            <Typography
              component="h1"
              variant="h5"
              color="error"
              align="center"
            >
              {errorMessage}
            </Typography>
            <Button variant="contained" onClick={() => navigate("/login")}>
              Quay lại đăng nhập
            </Button>
          </Card>
        </SignInContainer>
      </AppTheme>
    );
  }
  if (success) {
    return (
      <AppTheme {...props}>
        <SignInContainer direction="column" justifyContent="center">
          <Card variant="outlined">
            <Typography
              component="h1"
              variant="h5"
              color="primary"
              align="center"
            >
              {successMessage}
            </Typography>
            <Button variant="contained" onClick={() => navigate("/login")}>
              Quay lại đăng nhập
            </Button>
          </Card>
        </SignInContainer>
      </AppTheme>
    );
  }

  return (
    <AppTheme {...props}>
      <SignInContainer direction="column" justifyContent="center">
        <AppBar
          position="static"
          component="div"
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            "& .MuiToolbar-root": {
              color: "text.primary",
            },
            "& .MuiButton-root": {
              color: "inherit",
            },
            position: "absolute",
            top: "0rem",
            right: "0rem",
          }}
        >
          <Toolbar>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}
            >
              <img
                src="/Relisoft1024_ngang.svg"
                alt="Logo"
                style={{ height: "40px", marginRight: "10px" }}
              />
            </Typography>

            {/* Menu cho màn hình lớn */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <Button component={Link} href="https://relipos.com/">
                Giới thiệu
              </Button>
              <Button component={Link} href="https://relipos.com/ho-tro/">
                Hướng dẫn
              </Button>
              <Button component={Link} href="https://www.youtube.com/@Relisoft">
                Video
              </Button>
              <Button component={Link} href="https://relipos.com/lien-he/">
                Liên hệ
              </Button>
            </Box>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}
            >
              <img
                src="Relisoft1024_ngang.svg"
                alt="Logo"
                style={{ height: "30px", marginRight: "10px" }}
              />
            </Typography>
          </Toolbar>
        </AppBar>
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            Đổi mật khẩu
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="password">Mật khẩu mới</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Xác nhận mật khẩu mới</FormLabel>
              <TextField
                error={confirmPasswordError}
                helperText={confirmPasswordErrorMessage}
                name="confirmPassword"
                placeholder="••••••"
                type="password"
                id="confirmPassword"
                required
                fullWidth
                variant="outlined"
                color={confirmPasswordError ? "error" : "primary"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
            >
              Đổi mật khẩu
            </Button>
          </Box>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}

export default ChangePasswordPage;
